/**
 * Nombres de cancha y MINI CANCHAS (Eduardo 2026-10-01: «en una categoría de
 * Mini cancha marco qué canchas se dividen; cada una sale como Cancha 9-A y
 * Cancha 9-B: dos partidos a la vez en la 9»).
 *
 * Desde 2026-10-08 una cancha se divide en 2, 3 o 4 mini canchas (A..D).
 * Una mini cancha es solo el texto de la cancha: "9-A" … "9-D" (número 1–50
 * más A, B, C o D). Si dos canchas chocan se decide SOLO con sus nombres, igual que
 * en el servidor (backend/app/core/courts.py), sin leer ajustes de categoría:
 * - la cancha completa "9" choca con "9" y con "9-A".."9-D";
 * - "9-A" choca con "9" y "9-A", pero NO con "9-B", "9-C" ni "9-D";
 * - cualquier otro nombre («Central», «Cancha 2», «Pádel-A») es una cancha
 *   completa opaca y choca solo consigo misma (sin mayúsculas ni espacios de
 *   más), como siempre.
 *
 * La tabla de casos compartida con el servidor es courtHalvesParityCases.json.
 */

export type CourtHalf = "A" | "B" | "C" | "D";

export const COURT_HALVES: readonly CourtHalf[] = ["A", "B", "C", "D"];

/** En cuántas mini canchas se puede partir una cancha (2 = lo de siempre). */
export type SplitParts = 2 | 3 | 4;
export const SPLIT_PARTS_OPTIONS: readonly SplitParts[] = [2, 3, 4];
export const DEFAULT_SPLIT_PARTS: SplitParts = 2;

/** 2, 3 o 4 (también "3"); cualquier otra cosa → 2. */
export function cleanSplitParts(value: unknown): SplitParts {
  const number = typeof value === "string" ? Number(value.trim()) : value;
  return number === 3 || number === 4 ? number : DEFAULT_SPLIT_PARTS;
}

/** Posición de la mini cancha: A = 0, B = 1, C = 2, D = 3. */
export function halfIndex(half: CourtHalf): number {
  return COURT_HALVES.indexOf(half);
}

/** Base más alta que puede dividirse (= #Canchas máximo del calendario). */
const MAX_HALF_BASE = 50;

const HALF_RE = /^0*(\d{1,2})\s*-\s*([A-Da-d])$/;
const WHOLE_NUMERIC_RE = /^[1-9]\d?$/;

/** Espacios colapsados (como `" ".join(s.split())` del servidor). */
export function normalizeCourt(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return trimmed ? trimmed.split(/\s+/).join(" ") : "";
}

/** "9 - a" → { base: "9", half: "A" }; "9", «Central» o «Pádel-A» → null. */
export function parseHalfCourt(value: string | null | undefined): { base: string; half: CourtHalf } | null {
  const match = HALF_RE.exec(normalizeCourt(value));
  if (!match) return null;
  const base = Number(match[1]);
  if (!Number.isInteger(base) || base < 1 || base > MAX_HALF_BASE) return null;
  return { base: String(base), half: match[2].toUpperCase() as CourtHalf };
}

/** Lo que se guarda: "9 - a" → "9-A"; "  Central " → "Central"; vacío → null. */
export function canonicalCourt(value: string | null | undefined): string | null {
  const half = parseHalfCourt(value);
  if (half) return `${half.base}-${half.half}`;
  const clean = normalizeCourt(value);
  return clean || null;
}

export function isHalfCourt(value: string | null | undefined): boolean {
  return parseHalfCourt(value) !== null;
}

/** "A".."D" de una mini cancha; null si es cancha completa. */
export function halfOf(value: string | null | undefined): CourtHalf | null {
  return parseHalfCourt(value)?.half ?? null;
}

/** Cancha física: "9-A" → "9"; cualquier otra, su nombre limpio. */
export function courtBase(value: string | null | undefined): string {
  return parseHalfCourt(value)?.base ?? normalizeCourt(value);
}

/** Llave para comparar: canónica en minúsculas ("9-a", "central"). */
export function courtKey(value: string | null | undefined): string {
  return (canonicalCourt(value) ?? "").toLowerCase();
}

/** Número 1–50 escrito sin ceros: la única cancha completa que tiene mitades. */
function wholeNumericBase(value: string | null | undefined): string | null {
  const clean = normalizeCourt(value);
  if (!WHOLE_NUMERIC_RE.test(clean)) return null;
  return Number(clean) <= MAX_HALF_BASE ? clean : null;
}

/** Llaves con las que choca una cancha (en este orden):
 * "9" → ["9", "9-a", "9-b", "9-c", "9-d"]; "9-A" → ["9", "9-a"];
 * «Central» → ["central"]. */
export function courtFamilyKeys(value: string | null | undefined): string[] {
  const half = parseHalfCourt(value);
  if (half) return [half.base, `${half.base}-${half.half.toLowerCase()}`];
  const numeric = wholeNumericBase(value);
  if (numeric) return [numeric, ...COURT_HALVES.map((half) => `${numeric}-${half.toLowerCase()}`)];
  const key = courtKey(value);
  return key ? [key] : [];
}

/** ¿Dos canchas comparten espacio físico? (simétrico; vacío nunca choca). */
export function courtsOverlap(a: string | null | undefined, b: string | null | undefined): boolean {
  const key = courtKey(b);
  if (!key) return false;
  return courtFamilyKeys(a).includes(key);
}

/** Las mini canchas de una base: "9" → ["9-A", "9-B"]; en 4 → ["9-A".."9-D"]. */
export function halfCourts(base: string, parts: number = DEFAULT_SPLIT_PARTS): string[] {
  const clean = normalizeCourt(base);
  return COURT_HALVES.slice(0, cleanSplitParts(parts)).map((half) => `${clean}-${half}`);
}

/** ¿Esta base (columna "1".."50") se puede dividir en mini canchas? */
export function isSplittableBase(value: string | null | undefined): boolean {
  return wholeNumericBase(value) !== null;
}

/** "1" → «Cancha 1», "9-a" → «Cancha 9-A», «Central» → «Cancha Central»;
 * «Cancha 2» se queda igual. */
export function courtDisplayName(value: string | null | undefined): string {
  const canonical = canonicalCourt(value);
  if (!canonical) return "Cancha";
  if (/cancha/i.test(canonical)) return canonical;
  return `Cancha ${canonical}`;
}

/** Cancha en las etiquetas de una línea (inicio del jugador y del admin,
 * Rondas) — el espejo de `court_notice_name` de los avisos del servidor:
 * "2" → «Cancha 2», "9-a" → «Cancha 9-A»; un nombre de texto («Central»,
 * «Pista 1», «Cancha 2») sale tal cual, como siempre y como en el aviso.
 * Vacío → "". El grid y los mensajes del admin usan courtDisplayName. */
export function courtNoticeName(value: string | null | undefined): string {
  const canonical = canonicalCourt(value);
  if (!canonical) return "";
  if (isHalfCourt(canonical) || wholeNumericBase(canonical) !== null) return courtDisplayName(canonical);
  return canonical;
}

/** Columnas "1".."N" a partir de #Canchas (vacío si no es un entero > 0). */
export function courtsFromCount(value: string | number | null | undefined): string[] {
  const count = Number(value);
  if (!Number.isInteger(count) || count <= 0) return [];
  return Array.from({ length: count }, (_, index) => String(index + 1));
}

/** Columnas del grid: cada base marcada se parte en sus mini canchas (2 por
 * omisión; `splitParts` dice 3 o 4).
 * ["1".."10"] con {"9","10"} → ["1",…,"8","9-A","9-B","10-A","10-B"]. Las
 * bases marcadas que no son columna se ignoran. */
export function courtColumns(
  bases: string[],
  splitBases: ReadonlySet<string>,
  splitParts?: ReadonlyMap<string, number>,
): string[] {
  const columns: string[] = [];
  for (const base of bases) {
    if (splitBases.has(base) && isSplittableBase(base)) columns.push(...halfCourts(base, splitParts?.get(base)));
    else columns.push(base);
  }
  return columns;
}

export interface CourtColumnPlacement {
  /** Índice de la columna (la primera, si abarca dos). */
  column: number;
  /** Columnas que abarca: 2..4 = cancha completa sobre sus mini canchas. */
  span: number;
  /** Mini cancha en una columna NO dividida: su carril (A = 0, B = 1,
   * C = 2, D = 3). */
  fixedLane?: number;
}

/** ¿Dónde se dibuja una cancha en estas columnas?
 * - la misma columna (sin mayúsculas ni espacios de más) → esa, 1 columna;
 * - "9" cuando las columnas son "9-A", "9-B" (… "9-D") juntas → todas;
 * - "9-A" cuando solo hay columna "9" → la 9, carril 0 (B: 1, C: 2, D: 3);
 * - nada de lo anterior → null (no cabe en el grid y se avisa). */
export function resolveCourtColumn(court: string | null | undefined, columns: string[]): CourtColumnPlacement | null {
  const key = courtKey(court);
  if (!key) return null;
  const columnKeys = columns.map((column) => courtKey(column));
  const exact = columnKeys.indexOf(key);
  if (exact >= 0) return { column: exact, span: 1 };
  const half = parseHalfCourt(court);
  if (half) {
    const whole = columnKeys.indexOf(half.base);
    if (whole >= 0) return { column: whole, span: 1, fixedLane: halfIndex(half.half) };
    return null;
  }
  const numeric = wholeNumericBase(court);
  if (numeric) {
    const left = columnKeys.indexOf(`${numeric}-a`);
    if (left >= 0 && columnKeys[left + 1] === `${numeric}-b`) {
      let span = 2;
      while (span < COURT_HALVES.length && columnKeys[left + span] === `${numeric}-${COURT_HALVES[span].toLowerCase()}`) {
        span += 1;
      }
      return { column: left, span };
    }
  }
  return null;
}

/** ¿Se ve esta cancha en estas columnas? (gate #146: lo que no se ve se
 * cuenta y se avisa). */
export function courtVisibleIn(court: string | null | undefined, columns: string[]): boolean {
  return resolveCourtColumn(court, columns) !== null;
}
